import React, { useState, useEffect } from 'react';
import BRAND from '../brand';
import { api, getTelegramUser, getImageUrl, pluralize, fetchWebUser } from '../api';
import CheckoutModal from '../components/CheckoutModal';
import SubscriptionCheckout from '../components/SubscriptionCheckout';
import { trackEvent } from '../utils/analytics';
import { canAskWriteAccess, markWriteAccessAsked, requestWriteAccessNative } from '../platform';
import NotifyPermissionPopup from '../components/NotifyPermissionPopup';
import { trackViewCart, trackBeginCheckout } from '../utils/metrika';
import { TG_BOT_USERNAME } from '../utils/attribution';
import { useCatalogLabels, genitivePlural, genitiveSingularLower, singularLower } from '../catalogLabels';
import { fetchAppSettings } from '../settings';
import { EmptyStateIcon, FieldIcon, SectionLoader, HeroIcon } from '../components/NavIcons';
import PhotoLightbox from '../components/PhotoLightbox';
import BouquetCompositionDetail from '../components/BouquetCompositionDetail';
import { formatDateRanges, minOrderDate, isoDate, describeDates, suggestReductions } from '../utils/deliveryDates';
import { useDeliverySlots } from '../deliverySlots';
import { nowInTZ, todayShop } from '../utils/timezone';

// Винительный падеж фразы (неодуш.): «Маттиола белая» → «маттиолу белую»,
// «Ирис синий» → «ирис синий», «Стрелиция» → «стрелицию». Эвристика по окончаниям —
// для цветочных названий вида «{существительное} {прилагательное-цвет}» работает.
function capitalizeFirst(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function toAccusative(phrase) {
  if (!phrase) return phrase;
  const words = phrase.split(' ').map((w, idx) => {
    const lw = (idx === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w);
    if (/ая$/.test(lw)) return lw.slice(0, -2) + 'ую';
    if (/яя$/.test(lw)) return lw.slice(0, -2) + 'юю';
    if (/я$/.test(lw)) return lw.slice(0, -1) + 'ю';
    if (/а$/.test(lw)) return lw.slice(0, -1) + 'у';
    return lw; // м.р./ср.р. неодуш. — винительный = именительный
  });
  return words.join(' ');
}

// Род первого слова названия → согласование «общий / общую / общее».
function obshchiyForm(name) {
  const first = (name || '').split(' ')[0].toLowerCase();
  if (/[ая]$/.test(first)) return 'общую';
  if (/[ое]$/.test(first)) return 'общее';
  return 'общий';
}

function CartPage({ cart, cartLoading, updateQuantity, updateBouquetQuantity, removeFromCart, goToMenu, goToOrders, clearCart, paymentSuccess, paymentCancelled, paidOrderId, resetPaymentSuccess, resetPaymentCancelled, onOrderPaidByBonuses, forceCloseCheckout, hydrationRemovedItems, onHydrationRemovedSeen, subscriptionCart, setSubscriptionCart, onSubscriptionCheckout, preferredCartTab, onSubscriptionInvoiceSuccess }) {
  const catalogLabels = useCatalogLabels();
  const slotsConfig = useDeliverySlots();
  // Точки самовывоза приходят только из API: пустой список = самовывоза нет,
  // чекаут в этом случае не показывает выбор способа получения (только доставка).
  const [pickupPoints, setPickupPoints] = useState([]);
  const [settings, setSettings] = useState({
    service_fee_percent: 10,
    bonus_percent: 3,
    postcard_price: 199
  });
  const [userBonuses, setUserBonuses] = useState(0);
  const [useBonuses, setUseBonuses] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  // Попап «разрешите боту писать» на ступени «Оформить»: хранит колбэк
  // открытия чекаута — он зовётся после ответа (и «Разрешить», и «Позже»)
  const [writeAccessNext, setWriteAccessNext] = useState(null);
  const [oddQuantity, setOddQuantity] = useState(false);
  const [showOddQuantityTooltip, setShowOddQuantityTooltip] = useState(false);
  const [showVaseTooltip, setShowVaseTooltip] = useState(false);
  const [hasPostcard, setHasPostcard] = useState(false);
  const [outOfStockToast, setOutOfStockToast] = useState(null); // { productName }
  // Quick-view позиции корзины (как в каталоге: фото + панель с ценой и ±).
  // Храним ссылку {kind, id}, а не снимок — панель показывает живое количество.
  const [photoView, setPhotoView] = useState(null); // { kind: 'product'|'bouquet'|'sub', id }
  const [bqDetails, setBqDetails] = useState({}); // кэш api.getBouquet по id — для состава в панели
  const [availableDates, setAvailableDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  // Per-item даты: для сообщения «нет пересечения дат — вот что у каждой позиции».
  // Формат: [{label, dates: Set<string>}]
  const [itemAvailability, setItemAvailability] = useState([]);
  const [showDateConflict, setShowDateConflict] = useState(false);
  // Состав корзины, на котором клиент уже нажал «Перейти к оформлению» и получил
  // «нет общей даты»: пока состав тот же — кнопка серая, изменил — снова активна.
  const [conflictCartKey, setConflictCartKey] = useState(null);
  // Подписка
  const [subDeliveryCount, setSubDeliveryCount] = useState(subscriptionCart?.delivery_count || 2);
  const [subOddQuantity, setSubOddQuantity] = useState(false);
  const [subUseBonuses, setSubUseBonuses] = useState(false);
  const [discountTiers, setDiscountTiers] = useState([]);
  // Таб корзины: если подписка есть — показываем подписку (пользователь только что добавил), иначе товары
  const [cartTab, setCartTab] = useState(preferredCartTab || (subscriptionCart ? 'subscription' : 'products'));
  const [showSubCheckout, setShowSubCheckout] = useState(false);
  const [subCheckoutData, setSubCheckoutData] = useState(null);

  // Переключение таба при клике "Перейти в корзину" из каталога
  useEffect(() => {
    if (preferredCartTab) {
      setCartTab(preferredCartTab);
    }
  }, [preferredCartTab]);

  // Трекаем вход в корзину в Метрике — один раз за сессию
  useEffect(() => {
    trackViewCart(cart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Авто-переключение таба при изменении подписки
  const prevSubCartRef = React.useRef(subscriptionCart);
  useEffect(() => {
    const hadSub = prevSubCartRef.current;
    prevSubCartRef.current = subscriptionCart;
    // Подписка только что появилась — переключаем на таб подписки
    if (subscriptionCart && !hadSub && cart.length > 0) {
      setCartTab('subscription');
    }
    // Подписка удалена — возвращаемся на товары
    if (!subscriptionCart && cartTab === 'subscription') {
      setCartTab('products');
    }
    // Реагируем только на изменение subscriptionCart; cart.length и cartTab читаем «текущие»
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionCart]);

  // Закрываем модалку при возврате из оплаты
  useEffect(() => {
    if (forceCloseCheckout > 0) {
      setShowCheckout(false);
    }
  }, [forceCloseCheckout]);

  // Тост при удалении товаров во время гидрации
  useEffect(() => {
    if (!hydrationRemovedItems || hydrationRemovedItems.length === 0) return;
    setOutOfStockToast({ messages: hydrationRemovedItems });
    setTimeout(() => setOutOfStockToast(null), 4000);
    if (onHydrationRemovedSeen) onHydrationRemovedSeen();
    // onHydrationRemovedSeen — callback из props, не оборачиваем
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationRemovedItems]);

  // Закрываем тултип при клике вне его
  useEffect(() => {
    if (!showOddQuantityTooltip) return;

    const handleClickOutside = () => {
      setShowOddQuantityTooltip(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showOddQuantityTooltip]);

  useEffect(() => {
    if (!showVaseTooltip) return;
    const handleClickOutside = () => setShowVaseTooltip(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showVaseTooltip]);

  useEffect(() => {
    loadData();
    // Одноразовая загрузка при маунте
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Доступные даты для всей корзины: пересечение «стебли OK» ∩ «каждый букет OK на дату».
  // Параллельно сохраняем per-item даты — для подробного сообщения о конфликте.
  useEffect(() => {
    if (cart.length === 0) { setAvailableDates([]); setItemAvailability([]); return; }
    // Reserved/qty=0 не проверяем — они не идут в заказ.
    const hydratedStems = cart.filter(item => item.price != null && (item.kind || 'product') === 'product' && item.quantity > 0 && !item.reserved_by);
    const bouquets = cart.filter(item => item.kind === 'bouquet' && item.price != null && item.quantity > 0 && !item.reserved_by);

    if (hydratedStems.length === 0 && bouquets.length === 0) {
      setAvailableDates([]);
      setItemAvailability([]);
      return;
    }
    // Даты режутся по отсечке в поясе магазина — пока настройки не пришли, не считаем
    // (эффект перезапустится, когда slotsConfig загрузится).
    if (!slotsConfig.loaded) return;

    setDatesLoading(true);
    // Per-item запросы: одна позиция = одна задача
    const tasks = [];
    // Объединённый спрос на склад по product_id: стебли + recipe-часть букетов
    // (компоненты × сколько букетов надо СОБРАТЬ из склада, т.е. сверх уже собранных
    // physical-экземпляров). Это убирает «ложную доступность», когда букет и стебли
    // независимо считают, что весь сток общего компонента — их.
    const stemDemand = {};
    const stemNames = [];
    for (const item of hydratedStems) {
      const pid = item.id || item.product_id;
      if (!pid) continue;
      stemDemand[pid] = (stemDemand[pid] || 0) + item.quantity;
      stemNames.push(`${item.name} × ${item.quantity}`);
    }
    // Сколько каждого product_id занято компонентами букетов (recipe-часть, сверх готовых).
    const bouquetUseByProduct = {};
    for (const b of bouquets) {
      const physAvail = b.physical_available_today || 0;
      const recipeUnits = Math.max(0, b.quantity - physAvail);
      if (recipeUnits <= 0) continue;
      for (const c of (b.components || [])) {
        bouquetUseByProduct[c.product_id] = (bouquetUseByProduct[c.product_id] || 0) + c.quantity * recipeUnits;
        stemDemand[c.product_id] = (stemDemand[c.product_id] || 0) + c.quantity * recipeUnits;
      }
    }
    // Детализация по стеблям. available — сколько доступно на даты букета (текущее окно):
    // currentStock минус занятое букетами, округлённое ВНИЗ до шага продажи (step).
    const stemsDetail = hydratedStems.map(item => {
      const pid = item.id || item.product_id;
      const step = item.step || 1;
      const cur = item.is_expired ? 0 : (item.currentStock || 0);
      const rawAvail = Math.max(0, cur - (bouquetUseByProduct[pid] || 0));
      const available = Math.floor(rawAvail / step) * step;  // кратно шагу
      return { name: item.name, requested: item.quantity, available, step, pid };
    });
    const demandItems = Object.entries(stemDemand).map(([pid, qty]) => ({ product_id: parseInt(pid), quantity: qty }));
    if (demandItems.length > 0) {
      const label = stemNames.length > 0
        ? `${catalogLabels.products} (${stemNames.join(', ')})`
        : `Компоненты ${(genitivePlural(catalogLabels.bouquets) || catalogLabels.bouquets).toLowerCase()}`;
      tasks.push({
        label,
        kind: 'stems',
        stems: stemsDetail,
        promise: api.request('/orders/available-dates', {
          method: 'POST',
          body: JSON.stringify({ items: demandItems, days: settings.catalog_horizon_days || 14 }),
        }).then(r => new Set(r.dates || [])).catch(() => null),
      });
    }
    // Букеты дополнительно проверяем на собственную доступность (physical-окно + recipe).
    for (const b of bouquets) {
      const bId = b.bouquet_id || b.id;
      tasks.push({
        label: `«${b.name}» × ${b.quantity}`,
        kind: 'bouquet',
        promise: api.getBouquetAvailableDates(bId, b.quantity, settings.catalog_horizon_days || 14)
          .then(r => new Set(r.dates || [])).catch(() => null),
      });
    }

    // Собственное окно стеблей (БЕЗ букетной нагрузки) — для отображения «доступно с N»
    // и действия «убрать букет». НЕ участвует в пересечении (это справочные даты).
    let stemsOwnPromise = Promise.resolve(null);
    if (stemNames.length > 0) {
      const ownItems = hydratedStems
        .map(item => ({ product_id: item.id || item.product_id, quantity: item.quantity }))
        .filter(i => i.product_id);
      stemsOwnPromise = api.request('/orders/available-dates', {
        method: 'POST',
        body: JSON.stringify({ items: ownItems, days: settings.catalog_horizon_days || 14 }),
      }).then(r => ({ all: new Set(r.dates || []), per: r.per_item || [] })).catch(() => null);
    }

    Promise.all([...tasks.map(t => t.promise), stemsOwnPromise]).then(allResults => {
      // Даты сервера — с учётом отсечки: после неё «сегодня» уже не принять (то же
      // правило, что в оформлении — minOrderDate).
      const minIso = isoDate(minOrderDate(
        nowInTZ(slotsConfig.shopTimezone), slotsConfig.timeSlots, slotsConfig.leadTimeMinutes));
      const clip = set => (set ? new Set([...set].filter(d => d >= minIso)) : set);
      const results = allResults.slice(0, tasks.length).map(clip);
      const stemsOwn = allResults[tasks.length];
      const stemsOwnDates = stemsOwn ? clip(stemsOwn.all) : null;
      // Окно КАЖДОГО стебля: без него два стебля с непересекающимися окнами (один только
      // сегодня, другой с плановой через неделю) были одной группой «Стебли», и корзина
      // пускала в оформление без предупреждения (30.09.2026).
      const perStem = stemsOwn ? hydratedStems.map(it => {
        const pid = it.id || it.product_id;
        const e = stemsOwn.per.find(x => Number(x.product_id) === Number(pid));
        return e ? {
          name: it.name, dates: clip(new Set(e.dates || [])), rawDates: e.dates || [],
          requested: it.quantity, step: it.step || 1, capacity: e.capacity || null,
        } : null;
      }).filter(Boolean) : [];
      const perItem = tasks.map((t, i) => ({
        label: t.label, kind: t.kind, stems: t.stems, dates: results[i],
        // Для стеблей окно отображения — собственная доступность (без букета).
        ownDates: t.kind === 'stems' ? stemsOwnDates : null,
        perStem: t.kind === 'stems' ? perStem : null,
      }));
      setItemAvailability(perItem.filter(p => p.dates != null));

      const valid = results.filter(s => s != null);
      // Если ни один запрос не прошёл (сеть/auth) — не блокируем оформление: проверка дат
      // повторится на стороне backend при создании заказа. Считаем «дата проверена позже».
      if (valid.length === 0) {
        setAvailableDates(null);
        return;
      }
      // Если в корзине только одна группа (только стебли или один букет), «общие даты» —
      // это просто даты этой группы. Конфликта быть не может, баннер не показываем.
      const intersection = [...valid[0]].filter(d => valid.every(s => s.has(d)));
      setAvailableDates(intersection);
      if (intersection.length > 0) {
        setShowDateConflict(false);
      }
    }).finally(() => setDatesLoading(false));
    // Горизонт каталога и подписи разделов — из настроек, могут прийти позже корзины:
    // без них в зависимостях даты оставались посчитанными на дефолтные 14 дней.
  }, [cart, slotsConfig, settings.catalog_horizon_days, catalogLabels.products, catalogLabels.bouquets]);

  // Re-fetch availability букетов при открытии корзины (защита от race condition:
  // другой клиент мог разобрать пока мы готовились оформить).
  // Эффективный максимум = max(today_available, next_better_capacity) — recipe-букет
  // может быть недоступен сегодня (component истёк), но доступен на дату плановой поставки.
  useEffect(() => {
    const bouquets = cart.filter(it => it.kind === 'bouquet' && it.price != null);
    if (bouquets.length === 0) return;
    Promise.all(bouquets.map(b =>
      api.getBouquet(b.bouquet_id || b.id).then(d => ({
        id: b.bouquet_id || b.id,
        max: Math.max(d.today_available || 0, d.next_better_capacity || 0),
      })).catch(() => null)
    )).then(results => {
      results.forEach(r => {
        if (!r) return;
        const inCart = cart.find(it => it.kind === 'bouquet' && (it.bouquet_id || it.id) === r.id);
        if (inCart && inCart.quantity > r.max && r.max >= 0) {
          const delta = inCart.quantity - r.max;
          if (delta > 0 && updateBouquetQuantity) {
            updateBouquetQuantity(r.id, -delta);
          }
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      // fetchAppSettings, а не api.getSettings: общий кэш на 10 секунд — свежесть ставок и
      // минимальной суммы сохраняется, но открытие корзины сразу после старта приложения
      // не шлёт ещё один такой же запрос (settings.js)
      const promises = [fetchAppSettings(), api.getPickupPoints()];
      if (subscriptionCart) promises.push(api.getSubscriptionDiscountTiers());
      const [settingsData, pointsData, tiersData] = await Promise.all(promises);
      setSettings(settingsData);
      setPickupPoints(pointsData);
      if (tiersData) {
        setDiscountTiers(tiersData.sort((a, b) => a.min_deliveries - b.min_deliveries));
      }

      const telegramUser = getTelegramUser();
      const platformId = telegramUser?.telegram_id || telegramUser?.max_id;
      if (platformId) {
        try {
          const userData = await api.getUser(platformId);
          setUserBonuses(userData.bonus_balance);
        } catch (e) {
          // Пользователь ещё не создан
        }
      } else if (telegramUser?.platform === 'web') {
        // Web-юзер (SMS-логин): идентичность и бонусы приходят из JWT (/web-auth/me),
        // а не по telegram_id/max_id — иначе бонусы не грузятся и кнопка списания скрыта.
        try {
          const wu = await fetchWebUser();
          if (wu) setUserBonuses(wu.bonus_balance || 0);
        } catch (e) { /* не залогинен */ }
      }
    } catch (error) {
      console.log('Using default settings:', error);
    }
  };

  // Расчёт сумм (фильтруем товары без цены — ещё не гидрированы)
  // Все гидрированные item'ы для рендера (включая reserved/qty=0).
  const displayCart = cart.filter(item => item.price != null && !isNaN(item.price));
  // Есть ли в корзине обычные стебли (не букеты) — для пункта «Нечётное количество».
  const hasStems = displayCart.some(item => (item.kind || 'product') === 'product');
  // Если стеблей не осталось (только букеты) — сбрасываем флаг, чтобы stale true не ушёл в заказ.
  useEffect(() => {
    if (!hasStems && oddQuantity) setOddQuantity(false);
  }, [hasStems, oddQuantity]);

  // Сколько каждого product_id занято компонентами букетов в корзине — нужно для
  // консервативного бейджа на строке стеблей: букет «съедает» и текущую, и плановую.
  const cartBouquetUsage = {};
  cart.forEach(it => {
    if (it.kind === 'bouquet' && it.components) {
      it.components.forEach(c => {
        cartBouquetUsage[c.product_id] = (cartBouquetUsage[c.product_id] || 0) + c.quantity * (it.quantity || 0);
      });
    }
  });
  // Для итого и чекаута берём только активные (qty>0, без reserved_by).
  const validCart = displayCart.filter(item => item.quantity > 0 && !item.reserved_by);
  // Раздельный subtotal: стебли (kind='product') и букеты (kind='bouquet')
  const stemsItems = validCart.filter(it => (it.kind || 'product') === 'product');
  const bouquetItems = validCart.filter(it => it.kind === 'bouquet');
  const stemsSubtotal = stemsItems.reduce((s, it) => s + it.price * it.quantity, 0);
  // Для букетов price = final_price (уже с сервисным сбором), base_price = сумма компонентов.
  // На карточке клиент видит final_price, поэтому в корзине строкой «Сумма букетов» показываем
  // тоже final_price (= base + fee). Отдельной строкой сбор для букетов НЕ показываем.
  const bouquetsSubtotalBase = bouquetItems.reduce((s, it) => s + (it.base_price != null ? it.base_price : it.price) * it.quantity, 0);
  const bouquetsSubtotalFinal = bouquetItems.reduce((s, it) => s + (it.price || 0) * it.quantity, 0);
  const postcardPrice = hasPostcard ? (settings.postcard_price ?? 0) : 0;
  // Ставки и цена открытки — только из /api/settings: сервер отдаёт их с теми же
  // дефолтами, что использует в расчёте заказа (services/pricing). Числа здесь не дублируем.
  const stemsFeePct = settings.service_fee_percent;
  const bouquetFeePct = settings.bouquet_service_fee_percent != null ? settings.bouquet_service_fee_percent : stemsFeePct;
  // Сервисный сбор НЕ начисляется на открытку — клиент платит её 1:1.
  const stemsServiceFee = Math.round(stemsSubtotal * stemsFeePct / 100);
  // Сбор за букеты — от суммы корзины, как считает сервер (_calc_service_fees).
  // Раньше он брался разностью «цена со сбором минус цена без сбора», а цену со
  // сбором API округляет по каждому букету отдельно. Округление не переживает
  // сложение: три букета по 103 ₽ при ставке 20 % давали 63 ₽ поштучно против
  // 62 ₽ от суммы — витрина показывала одно, сервер выставлял другое.
  const bouquetServiceFee = Math.round(bouquetsSubtotalBase * bouquetFeePct / 100);
  const serviceFee = stemsServiceFee + bouquetServiceFee;
  // Сумма заказа (для расчёта итого / бонусов) = стебли + букеты(база) + открытка.
  const productsTotal = stemsSubtotal + bouquetsSubtotalBase;
  const subtotal = productsTotal + postcardPrice;
  const bonusDiscount = useBonuses ? Math.min(userBonuses, subtotal + serviceFee) : 0;
  const total = subtotal + serviceFee - bonusDiscount;
  // Кэшбэк округляется вверх — как на сервере (pricing.cashback). Здесь стоял
  // Math.floor, а на сервере усечение; теперь обе стороны считают одинаково.
  const bonusesEarned = useBonuses ? 0 : Math.round(total * settings.bonus_percent / 100);

  // Подписка: макс. доставок = дни в окне 2 месяцев, по «сегодня» магазина
  // (пока настройки не пришли — без ограничения, их ждёт и само оформление)
  let maxSubDeliveries = Infinity;
  if (slotsConfig.loaded) {
    const _today = todayShop(slotsConfig.shopTimezone);
    const _minDate = new Date(_today.getFullYear(), _today.getMonth(), _today.getDate() + 3);
    const _maxDate = new Date(_today.getFullYear(), _today.getMonth() + 2, _today.getDate());
    maxSubDeliveries = Math.max(2, Math.floor((_maxDate - _minDate) / (1000 * 60 * 60 * 24)) + 1);
  }
  const vaseMinDeliveries = settings.vase_min_deliveries || 4;

  // Подписка: расчёты
  const getSubDiscountPercent = () => {
    let percent = 0;
    for (const tier of discountTiers) {
      if (subDeliveryCount >= tier.min_deliveries) percent = tier.discount_percent;
    }
    return percent;
  };
  const subItem = subscriptionCart?.catalog_item;
  const subSubtotal = subItem ? subItem.price * subDeliveryCount : 0;
  const subDiscountPercent = getSubDiscountPercent();
  // Скидка и сбор — по доставке, умноженные на число доставок: так считает
  // сервер (единица учёта подписки — доставка). Процент от общей суммы давал
  // расхождение в рубль-два с суммой заказов подписки.
  const subDiscountPerDelivery = subItem ? Math.floor(subItem.price * subDiscountPercent / 100) : 0;
  const subDiscountAmount = subDiscountPerDelivery * subDeliveryCount;
  const subServiceFee = subItem ? Math.round((subItem.price - subDiscountPerDelivery) * stemsFeePct / 100) * subDeliveryCount : 0;
  const subBonusDiscount = subUseBonuses ? Math.min(userBonuses, subSubtotal - subDiscountAmount + subServiceFee) : 0;
  const subTotal = subSubtotal - subDiscountAmount + subServiceFee - subBonusDiscount;
  const subBonusesEarned = subUseBonuses ? 0 : Math.round(subTotal * settings.bonus_percent / 100);

  // Проверка минимальной суммы заказа (товары + открытка + сервисный сбор)
  const orderAmountForMinCheck = subtotal + serviceFee;
  const minOrderAmount = settings.min_order_amount || 1500;
  const isOrderAmountValid = validCart.length === 0 || orderAmountForMinCheck >= minOrderAmount;
  const amountDifference = minOrderAmount - orderAmountForMinCheck;

  // hasNoDates = «конфликт дат между позициями». Условия:
  //  • запросы прошли (availableDates != null — null означает «не смогли проверить»),
  //  • есть товары в корзине,
  //  • > 1 позиции (иначе пересекать нечего — конфликта быть не может),
  //  • пересечение пустое.
  //  Позиции: каждый букет + КАЖДЫЙ стебель (два стебля без общей даты — тоже конфликт).
  const positionsCount = itemAvailability.reduce(
    (n, it) => n + (it.kind === 'stems' ? Math.max(1, (it.perStem || []).length) : 1), 0);
  const hasNoDates =
    !datesLoading &&
    validCart.length > 0 &&
    Array.isArray(availableDates) &&
    availableDates.length === 0 &&
    positionsCount > 1;
  // Кнопка оформления серая, пока клиент не изменит состав после «нет общей даты»
  const cartKey = cart.map(it => `${it.kind || 'product'}:${it.id}:${it.quantity}`).join('|');
  const conflictLocked = hasNoDates && conflictCartKey === cartKey;
  const canCheckout = isOrderAmountValid && !conflictLocked;

  const handleOrderSuccess = (order) => {
    setShowCheckout(false);
    // Очищаем корзину всегда — независимо от статуса оплаты
    if (clearCart) {
      clearCart();
    }
    // Дополнительно очищаем localStorage напрямую
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('stems_cart_v2_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.log('Error clearing cart from localStorage:', e);
    }
    if (order.payment_status === 'paid' && onOrderPaidByBonuses) {
      // Оплачено бонусами / самовывоз — показываем тост
      onOrderPaidByBonuses(order.order_number || order.id);
    } else if (order.payment_status !== 'paid' && goToOrders) {
      // Уходим в Мои заказы пока открывается браузер с оплатой
      goToOrders();
    }
  };

  // Получить шаг для товара
  const getStep = (item) => item.step || 1;

  // Показываем сообщение об отмене оплаты
  if (paymentCancelled) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <div className="empty-cart-box">
            <div style={{ marginBottom: '16px' }}>
              <HeroIcon name="unpaid" size={64} />
            </div>
            <h2>Вы не оплатили заказ</h2>
            <p style={{ fontSize: '18px', marginTop: '12px', color: '#666' }}>
              Мы грустим ;(
            </p>
            <p style={{ fontSize: '14px', marginTop: '20px', color: '#999' }}>
              Сейчас вернём вас в корзину...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Показываем экран после успешной оплаты (возврат с платёжки)
  if (paymentSuccess) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <div className="empty-cart-box">
            <div style={{ marginBottom: '16px' }}>
              <HeroIcon name="paid" size={64} />
            </div>
            <h2>Ваш заказ успешно оплачен!</h2>
            {paidOrderId && (
              <p style={{ fontSize: '16px', marginTop: '12px' }}>
                Заказ #{paidOrderId}
              </p>
            )}
            <button className="primary-btn" onClick={() => { resetPaymentSuccess(); goToMenu(); }}>
              Вернуться в каталог
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Полноэкранный чекаут (вместо модалки)
  if (showCheckout) {
    const handleOutOfStock = async (items) => {
      setShowCheckout(false);
      const affectedIds = items.map(i => i.productId);
      let freshMap = {};
      try {
        const fresh = await api.getProductsByIds(affectedIds);
        fresh.forEach(p => { freshMap[p.id] = p; });
      } catch (e) {}
      const messages = [];
      items.forEach(({ productId, productName }) => {
        const item = cart.find(i => i.id === productId);
        if (!item) return;
        const freshProduct = freshMap[productId];
        if (!freshProduct || freshProduct.is_hidden) {
          updateQuantity(productId, -item.quantity, 0, 0, []);
          messages.push({ name: productName, available: 0, gone: true });
          return;
        }
        const currentQty = freshProduct.quantity || 0;
        const futureStock = freshProduct.future_stock || [];
        const step = item.step || 1;
        // ПЕРВАЯ плановая ПО ДАТЕ с любым доступным остатком (с учётом шага) —
        // согласованно с правилом «лимит из текущего ИЛИ первой плановой».
        const nearestFuture = futureStock.slice()
          .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
          .find(fs => Math.floor((fs.available || 0) / step) * step > 0) || null;
        const futureQty = nearestFuture ? (nearestFuture.available || 0) : 0;
        const totalAvailable = Math.max(currentQty, futureQty);
        if (totalAvailable === 0) {
          updateQuantity(productId, -item.quantity, 0, currentQty, futureStock);
          messages.push({ name: productName, available: 0, gone: true });
        } else {
          const newQty = Math.min(item.quantity, Math.floor(totalAvailable / step) * step);
          if (newQty > 0) {
            // Передаём свежий currentQty + future_stock — карточка в корзине обновит
            // и «В наличии: N шт», и «С DD.MM: M шт» (плановая).
            updateQuantity(productId, newQty - item.quantity, totalAvailable, currentQty, futureStock);
            if (newQty < item.quantity) {
              messages.push({ name: productName, available: newQty, gone: false });
            }
          } else {
            updateQuantity(productId, -item.quantity, 0, currentQty, futureStock);
            messages.push({ name: productName, available: 0, gone: true });
          }
        }
      });
      if (messages.length > 0) {
        setOutOfStockToast({ messages });
        setTimeout(() => setOutOfStockToast(null), 4000);
      }
    };

    return (
      <CheckoutModal
        cart={cart}
        total={total}
        serviceFee={serviceFee}
        subtotal={subtotal}
        stemsSubtotal={stemsSubtotal}
        stemsServiceFee={stemsServiceFee}
        bouquetsSubtotalFinal={bouquetsSubtotalFinal}
        bouquetsSubtotalBase={bouquetsSubtotalBase}
        bouquetServiceFee={bouquetServiceFee}
        pickupPoints={pickupPoints}
        useBonuses={useBonuses}
        bonusDiscount={bonusDiscount}
        userBonuses={userBonuses}
        bonusesEarned={bonusesEarned}
        oddQuantity={oddQuantity}
        hasPostcard={hasPostcard}
        postcardPrice={postcardPrice}
        onClose={() => setShowCheckout(false)}
        onSuccess={handleOrderSuccess}
        onOrderCreated={() => {}}
        onOutOfStock={handleOutOfStock}
      />
    );
  }

  // Полноэкранный чекаут подписки
  if (showSubCheckout && subCheckoutData) {
    return (
      <SubscriptionCheckout
        subscriptionData={subCheckoutData}
        onBack={() => setShowSubCheckout(false)}
        onSuccess={(subscription, paymentType) => {
          setSubscriptionCart(null);
          setShowSubCheckout(false);
          setSubCheckoutData(null);
          if (paymentType === 'invoice' && onSubscriptionInvoiceSuccess) {
            onSubscriptionInvoiceSuccess(subscription.order_number || subscription.id);
          } else if (paymentType === 'bonuses' && onOrderPaidByBonuses) {
            // Бонусы покрыли подписку целиком — тот же тост, что у заказа
            onOrderPaidByBonuses(subscription.order_number || subscription.id);
          } else if (paymentType === 'card' && goToOrders) {
            goToOrders();
          }
        }}
      />
    );
  }

  if (cartLoading) {
    return (
      <div className="cart-page">
        <SectionLoader />
      </div>
    );
  }

  if (displayCart.length === 0 && !subscriptionCart && !outOfStockToast) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <div className="empty-cart-box">
            <span className="empty-cart-icon"><EmptyStateIcon name="cart" size={56} /></span>
            <h2>Ваша корзина пуста</h2>
            <p>Добавьте свежие стебли, чтобы сделать ваш день ярче 🌿</p>
            <button className="primary-btn" onClick={goToMenu}>
              Перейти в каталог
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Открытие quick-view позиции. Для букета состав грузим ДО показа окна —
  // иначе панель «доезжает» и фото прыгает (та же логика, что в BouquetCard).
  const openPhotoView = (item) => {
    const isBouquet = item.kind === 'bouquet';
    const kind = isBouquet ? 'bouquet' : 'product';
    const id = isBouquet ? (item.bouquet_id || item.id) : item.id;
    if (isBouquet && !bqDetails[id]) {
      api.getBouquet(id)
        .then(d => setBqDetails(prev => ({ ...prev, [id]: d })))
        .catch(() => {})
        .finally(() => setPhotoView({ kind, id }));
      return;
    }
    setPhotoView({ kind, id });
  };

  // Получаем изображение товара; тап по миниатюре — quick-view
  const getItemImage = (item) => {
    if (item.images && item.images.length > 0 && item.images[0].image_url) {
      return (
        <img
          src={getImageUrl(item.images[0].image_url)}
          alt={item.name}
          onClick={() => openPhotoView(item)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        />
      );
    }
    return item.emoji || '💐';
  };

  return (
    <div className="cart-page cart-page-filled">
      {(() => {
        // Quick-view позиции корзины: позицию ищем в живой корзине, чтобы
        // количество и цена в панели обновлялись при нажатиях ±. Если позицию
        // удалили из корзины — окно закрывается само (рендерим null).
        if (!photoView) return null;
        const pv = photoView;
        const pvItem = pv.kind === 'sub'
          ? subItem
          : pv.kind === 'bouquet'
            ? displayCart.find(it => it.kind === 'bouquet' && (it.bouquet_id || it.id) === pv.id)
            : displayCart.find(it => (it.kind || 'product') === 'product' && it.id === pv.id);
        if (!pvItem || !pvItem.images || pvItem.images.length === 0 || !pvItem.images[0].image_url) return null;

        let panel;
        if (pv.kind === 'sub') {
          panel = (
            <>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{pvItem.name}</div>
              {pvItem.description && (
                <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {pvItem.description}
                </div>
              )}
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 20, fontWeight: 800 }}>
                  {(pvItem.price * subDeliveryCount).toLocaleString('ru-RU')}₽
                </span>
                <span className="product-step-badge" style={{ marginLeft: 8 }}>
                  за {subDeliveryCount} {pluralize(subDeliveryCount, 'доставку', 'доставки', 'доставок')}
                </span>
              </div>
              {/* Количество доставок — как в строке подписки корзины */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button className="primary-btn" style={{ padding: '8px 16px' }} onClick={() => {
                  if (subDeliveryCount > 2) {
                    const nc = subDeliveryCount - 1;
                    setSubDeliveryCount(nc);
                    setSubscriptionCart({ ...subscriptionCart, delivery_count: nc });
                  } else {
                    setSubscriptionCart(null); // ниже 2 — подписка убирается, окно закроется само
                  }
                }}>−</button>
                <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{subDeliveryCount}</span>
                <button className="primary-btn" style={{ padding: '8px 16px', opacity: subDeliveryCount >= maxSubDeliveries ? 0.4 : 1 }}
                  disabled={subDeliveryCount >= maxSubDeliveries}
                  onClick={() => {
                    const nc = Math.min(maxSubDeliveries, subDeliveryCount + 1);
                    setSubDeliveryCount(nc);
                    setSubscriptionCart({ ...subscriptionCart, delivery_count: nc });
                  }}>+</button>
              </div>
            </>
          );
        } else {
          const isBouquet = pv.kind === 'bouquet';
          const step = isBouquet ? 1 : getStep(pvItem);
          const availableQuantity = pvItem.availableQuantity || 999;
          const canAdd = pvItem.quantity + step <= availableQuantity;
          const base = isBouquet ? (pvItem.base_price != null ? pvItem.base_price : pvItem.price) : pvItem.price;
          const unit = isBouquet ? (pvItem.price != null ? pvItem.price : base + Math.round(base * bouquetFeePct / 100)) : pvItem.price;
          const inc = () => {
            if (!canAdd) return;
            if (isBouquet) updateBouquetQuantity?.(pvItem.bouquet_id || pvItem.id, 1);
            else updateQuantity(pvItem.id, step);
          };
          const dec = () => {
            if (isBouquet) updateBouquetQuantity?.(pvItem.bouquet_id || pvItem.id, -1);
            else updateQuantity(pvItem.id, -step);
          };
          // Плашки наличия — как в каталоге. Стебли: currentStock (сток на момент
          // добавления, реконсилится с каталогом) + первая плановая поставка из
          // future_stock. Букеты: сегодня/следующая волна из полей карточки.
          const curStock = Math.floor((pvItem.currentStock ?? 0) / step) * step;
          const nearestFuture = !isBouquet
            ? ((pvItem.future_stock || [])
                .slice()
                .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                .find(fs => Math.floor((fs.available || 0) / step) * step > 0) || null)
            : null;
          const bToday = pvItem.effective_today != null ? pvItem.effective_today
            : (pvItem.today_available != null ? pvItem.today_available : (pvItem.max_orderable || 0));
          const bDate = pvItem.effective_future_date != null ? pvItem.effective_future_date : pvItem.next_better_date;
          const bCap = pvItem.effective_future_capacity != null ? pvItem.effective_future_capacity : (pvItem.next_better_capacity || 0);
          const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
          panel = (
            <>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                {pvItem.name}
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, padding: '2px 6px', background: '#F4F8F6', color: '#1F3D36', borderRadius: 8, verticalAlign: 'middle' }}>
                  {isBouquet ? singularLower(catalogLabels.bouquets) : (catalogLabels.products || 'стебли').toLowerCase()}
                </span>
              </div>
              {isBouquet && (
                <BouquetCompositionDetail
                  detail={bqDetails[pv.id]}
                  title={`Состав ${genitiveSingularLower(catalogLabels.bouquets) || ''}`.trim()}
                />
              )}
              <div className="stock-labels" style={{ marginBottom: 10 }}>
                {isBouquet ? (
                  <>
                    {bToday > 0 && (
                      <span className="stock-label stock-label--current">Готовы к сборке: {bToday} шт</span>
                    )}
                    {bDate && bCap > bToday && (
                      <span className="stock-label stock-label--future">С {fmtDate(bDate)}: {bCap} шт</span>
                    )}
                  </>
                ) : (
                  <>
                    {curStock >= step && (
                      <span className="stock-label stock-label--current">В наличии: {curStock} шт</span>
                    )}
                    {nearestFuture && (
                      <span className="stock-label stock-label--future">
                        С {fmtDate(nearestFuture.date)}: {Math.floor((nearestFuture.available || 0) / step) * step} шт
                      </span>
                    )}
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <span style={{ fontSize: 20, fontWeight: 800 }}>{(unit * pvItem.quantity).toLocaleString('ru-RU')}₽</span>
                  <span className="product-step-badge" style={{ marginLeft: 8 }}>за {pvItem.quantity} шт</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button className="primary-btn" style={{ padding: '8px 16px' }} onClick={dec}>−</button>
                  <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{pvItem.quantity}</span>
                  <button className="primary-btn" style={{ padding: '8px 16px', opacity: canAdd ? 1 : 0.4 }}
                    onClick={inc} disabled={!canAdd}>+</button>
                </div>
              </div>
            </>
          );
        }

        return (
          <PhotoLightbox
            images={pvItem.images.map(img => getImageUrl(img.image_large || img.image_url))}
            previews={pvItem.images.map(img => getImageUrl(img.image_medium || img.image_url))}
            alt={pvItem.name}
            onClose={() => setPhotoView(null)}
            panel={panel}
          />
        );
      })()}
      {/* Табы если есть и товары и подписка */}
      {validCart.length > 0 && subscriptionCart && (
        <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd', margin: '12px 16px 16px' }}>
          <button onClick={() => setCartTab('products')} style={{
            flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: cartTab === 'products' ? 600 : 400,
            background: cartTab === 'products' ? 'var(--primary-color)' : '#fff', color: 'var(--on-primary)',
          }}>{catalogLabels.products} ({validCart.length})</button>
          <button onClick={() => setCartTab('subscription')} style={{
            flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: cartTab === 'subscription' ? 600 : 400,
            background: cartTab === 'subscription' ? 'var(--primary-color)' : '#fff', color: 'var(--on-primary)', borderLeft: '1px solid #ddd',
          }}>{catalogLabels.subscription}</button>
        </div>
      )}

      {/* ===== СЕКЦИЯ ТОВАРОВ ===== */}
      {displayCart.length > 0 && (cartTab === 'products' || !subscriptionCart) && (
      <>
      <div className="cart-items">
        {displayCart.map(item => {
          const isBouquet = item.kind === 'bouquet';
          const step = isBouquet ? 1 : getStep(item);
          const availableQuantity = item.availableQuantity || 999;
          const canAdd = item.quantity + step <= availableQuantity;
          // «−» всегда доступен, пока в корзине > 0: количество может быть не кратно
          // шагу (шаг сменили в админке / реконсиляция обрезала) — раньше при qty < step
          // кнопка гасла и позицию нельзя было убрать степпером.
          const canRemove = item.quantity > 0;
          const isReserved = !!item.reserved_by;
          const isInactive = isReserved || item.quantity === 0;

          const handleInc = () => {
            if (isBouquet) updateBouquetQuantity?.(item.bouquet_id || item.id, 1);
            else updateQuantity(item.id, step);
          };
          const handleDec = () => {
            if (isBouquet) updateBouquetQuantity?.(item.bouquet_id || item.id, -1);
            // Последний шаг доводит ровно до 0 (updateQuantity сам удалит строку,
            // либо оставит qty=0 «Не участвует», если intended_quantity ещё > 0).
            else updateQuantity(item.id, -Math.min(step, item.quantity));
          };
          const handleRemove = () => {
            removeFromCart?.(isBouquet ? 'bouquet' : 'product', isBouquet ? (item.bouquet_id || item.id) : item.id);
          };

          if (isInactive) {
            // Букет в корзине съел компонент → «Забронирован под букеты в корзине».
            // Иначе — qty=0 без активного резерва, лежит как «не участвует».
            const pid = isBouquet ? null : (item.product_id || item.id);
            const tiedToBouquet = !isBouquet && pid && (cartBouquetUsage[pid] || 0) > 0;
            const label = (isReserved || tiedToBouquet)
              ? 'Пересекается с другими позициями в корзине'
              : 'Не участвует в заказе';
            const labelColor = (isReserved || tiedToBouquet) ? '#8C5A1E' : '#6B6B68';
            return (
              <div key={`${isBouquet ? 'b' : 'p'}-${item.id}`} className="cart-item" style={{ opacity: 0.55, background: '#FAFAFA' }}>
                <div className="cart-item-image">
                  {getItemImage(item)}
                </div>
                <div className="cart-item-info">
                  <h3 className="cart-item-name" style={{ color: '#6B6B68' }}>
                    {item.name}
                  </h3>
                  <div style={{ fontSize: 12, color: labelColor, marginTop: 4, lineHeight: 1.4 }}>
                    {label}
                  </div>
                </div>
                <button
                  onClick={handleRemove}
                  title="Удалить"
                  style={{
                    width: 32, height: 32, border: 'none', background: 'transparent',
                    cursor: 'pointer', fontSize: 22, fontWeight: 600, lineHeight: 1,
                    color: '#6B6B68', alignSelf: 'center', marginRight: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >×</button>
              </div>
            );
          }

          return (
            <div key={`${isBouquet ? 'b' : 'p'}-${item.id}`} className="cart-item">
              <div className="cart-item-image">
                {getItemImage(item)}
              </div>
              <div className="cart-item-info">
                <h3 className="cart-item-name">
                  {item.name}
                  {isBouquet
                    ? <span style={{ marginLeft: 6, fontSize: 11, padding: '2px 6px', background: '#F4F8F6', color: '#1F3D36', borderRadius: 8 }}>{singularLower(catalogLabels.bouquets)}</span>
                    : <span style={{ marginLeft: 6, fontSize: 11, padding: '2px 6px', background: '#F4F8F6', color: '#1F3D36', borderRadius: 8 }}>{(catalogLabels.products || 'стебли').toLowerCase()}</span>
                  }
                </h3>
                <div className="cart-item-price">
                  {(() => {
                    // Для букета показываем КОНЕЧНУЮ цену = final_price с бэка (источник правды),
                    // фолбэк — база + сервисный сбор.
                    const base = isBouquet ? (item.base_price != null ? item.base_price : item.price) : item.price;
                    const unit = isBouquet ? (item.price != null ? item.price : base + Math.round(base * bouquetFeePct / 100)) : item.price;
                    return (unit * item.quantity).toLocaleString('ru-RU');
                  })()} ₽
                </div>
                {isBouquet ? (() => {
                  // Cart-aware числа (штампует reconcileCart) — синхрон с карточкой каталога.
                  // Фоллбэк на сырые backend-значения, если профиль ещё не успел проштамповаться.
                  const today = item.effective_today != null ? item.effective_today : (item.today_available || 0);
                  const nextDate = item.effective_future_date !== undefined ? item.effective_future_date : item.next_better_date;
                  const nextCap = item.effective_future_capacity != null ? item.effective_future_capacity : (item.next_better_capacity || 0);
                  return (
                    <>
                      <div className="stock-labels" style={{ marginTop: 2, marginBottom: 0 }}>
                        {today > 0 && (
                          <span className="stock-label stock-label--current">
                            Готовы к сборке: {today} шт
                          </span>
                        )}
                        {nextDate && nextCap > 0 && (
                          <span className="stock-label stock-label--future">
                            С {new Date(nextDate + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {nextCap} шт
                          </span>
                        )}
                        {today === 0 && !(nextDate && nextCap > 0) && (
                          <span className="stock-label" style={{ background: '#F5F5F5', color: '#6B6B68' }}>
                            Под заказ
                          </span>
                        )}
                      </div>
                    </>
                  );
                })() : (() => {
                  // Вычитаем букетную «занятость» (и из текущей, и из плановой) — клиент
                  // видит то что реально может остаться для стеблей при любой дате доставки.
                  const pid = item.product_id || item.id;
                  const bouquetUse = cartBouquetUsage[pid] || 0;
                  const stockQty = item.is_expired ? 0 : Math.max(0, (item.currentStock || 0) - bouquetUse);
                  const adjFuture = (item.future_stock || []).map(fs => ({
                    ...fs,
                    available: Math.max(0, (fs.available || 0) - bouquetUse),
                  }));
                  const nearestFuture = adjFuture.slice()
                    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                    .find(fs => Math.floor((fs.available || 0) / step) * step > 0) || null;
                  const displayStockQty = Math.floor(stockQty / step) * step;
                  return (
                    <div className="stock-labels" style={{ marginTop: 2, marginBottom: 0 }}>
                      {displayStockQty > 0 && (
                        <span className="stock-label stock-label--current">
                          В наличии: {displayStockQty} шт
                        </span>
                      )}
                      {nearestFuture && Math.floor((nearestFuture.available || 0) / step) * step > 0 && (
                        <span className="stock-label stock-label--future">
                          С {new Date(nearestFuture.date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {Math.floor((nearestFuture.available || 0) / step) * step} шт
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="cart-item-quantity">
                <span
                  className={`quantity-btn ${!canRemove ? 'disabled' : ''}`}
                  onClick={() => canRemove && handleDec()}
                  style={{ opacity: canRemove ? 1 : 0.4 }}
                >
                  −
                </span>
                <span className="quantity">{item.quantity}</span>
                <span
                  className={`quantity-btn ${!canAdd ? 'disabled' : ''}`}
                  onClick={() => canAdd && handleInc()}
                  style={{ opacity: canAdd ? 1 : 0.4 }}
                >
                  +
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Нечётное количество — только если в корзине есть стебли (не только букеты) */}
      {hasStems && (
      <div className="cart-section" style={{
        background: '#FAFAFA',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
            <input
              type="checkbox"
              checked={oddQuantity}
              onChange={(e) => setOddQuantity(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', color: '#333' }}>
              Нечётное количество
              <span style={{ fontSize: '12px', color: '#8C8C8C', fontWeight: 400, marginLeft: 6 }}>
                (только для {(genitivePlural(catalogLabels.products) || catalogLabels.products).toLowerCase()})
              </span>
            </span>
          </label>
          <div style={{ position: 'relative' }}>
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowOddQuantityTooltip(!showOddQuantityTooltip);
              }}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                display: 'inline-flex',
                color: 'var(--text-secondary)'
              }}
            >
              <FieldIcon name="info" size={17} />
            </span>
            {showOddQuantityTooltip && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: '28px',
                width: '200px',
                background: '#333',
                color: '#fff',
                padding: '10px',
                borderRadius: '8px',
                fontSize: '12px',
                lineHeight: '1.3',
                zIndex: 100,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                Сделаем количество стеблей нечётным — уберём один из охапки.
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '8px',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid #333'
                }} />
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Открытка с пожеланием */}
      <div className="cart-section" style={{
        background: '#FAFAFA',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '12px'
      }}>
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={hasPostcard}
              onChange={(e) => setHasPostcard(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', color: '#333' }}>Открытка с пожеланием</span>
          </div>
          <span style={{ fontSize: '14px', color: hasPostcard ? '#2E7D32' : '#666', fontWeight: hasPostcard ? 500 : 400 }}>
            +{(settings.postcard_price ?? 0).toLocaleString('ru-RU')} ₽
          </span>
        </label>
      </div>

      {/* Раскладка: стебли (subtotal + сервисный сбор), букеты (final с уже включённым сбором), открытка */}
      <div className="cart-section">
        {stemsSubtotal > 0 && (
          <>
            <div className="cart-row">
              <span className="cart-label">{genitivePlural(catalogLabels.products)
                ? `Сумма заказа ${genitivePlural(catalogLabels.products).toLowerCase()}`
                : `Сумма заказа (${catalogLabels.products})`}</span>
              <span className="cart-value">{stemsSubtotal.toLocaleString('ru-RU')} ₽</span>
            </div>
            <div className="cart-row">
              <span className="cart-label">{`Сервисный сбор за ${catalogLabels.products.toLowerCase()}`}</span>
              <span className="cart-value">{stemsServiceFee.toLocaleString('ru-RU')} ₽</span>
            </div>
          </>
        )}
        {bouquetsSubtotalBase > 0 && (() => {
          // Склонение по СУММЕ количества букетов (не количеству OrderItem'ов):
          // 1 → «букета», 2+ → «букетов».
          const bouquetCount = bouquetItems.reduce((s, it) => s + (it.quantity || 0), 0);
          return (
            <>
              <div className="cart-row">
                <span className="cart-label">{(() => {
                  // «Сумма заказа сетов/сета» — от настраиваемого названия раздела
                  const gen = bouquetCount > 1
                    ? (genitivePlural(catalogLabels.bouquets) || '').toLowerCase()
                    : genitiveSingularLower(catalogLabels.bouquets);
                  return gen ? `Сумма заказа ${gen}` : `Сумма заказа (${catalogLabels.bouquets})`;
                })()}</span>
                <span className="cart-value">{bouquetsSubtotalBase.toLocaleString('ru-RU')} ₽</span>
              </div>
              {bouquetServiceFee > 0 && (
                <div className="cart-row">
                  <span className="cart-label">{`Сервисный сбор за ${catalogLabels.bouquets.toLowerCase()}`}</span>
                  <span className="cart-value">{bouquetServiceFee.toLocaleString('ru-RU')} ₽</span>
                </div>
              )}
            </>
          );
        })()}
        {postcardPrice > 0 && (
          <div className="cart-row">
            <span className="cart-label">Открытка</span>
            <span className="cart-value">{postcardPrice.toLocaleString('ru-RU')} ₽</span>
          </div>
        )}
        {bonusDiscount > 0 && (
          <div className="cart-row">
            <span className="cart-label">Со счёта {BRAND.name}</span>
            <span className="cart-value cart-bonus-discount">−{bonusDiscount.toLocaleString('ru-RU')} ₽</span>
          </div>
        )}
      </div>

      {/* Списать бонусы */}
      {userBonuses > 0 && (
        <div className="cart-section cart-bonus-section">
          <label className="bonus-checkbox">
            <input
              type="checkbox"
              checked={useBonuses}
              onChange={(e) => setUseBonuses(e.target.checked)}
            />
            <span className="bonus-checkbox-label">
              Списать {Math.min(userBonuses, subtotal + serviceFee).toLocaleString('ru-RU')} ₽ со счёта
            </span>
          </label>
        </div>
      )}

      {/* Итого */}
      <div className="cart-section cart-total-section">
        <div className="cart-row cart-total-row">
          <span className="cart-label">Итого:</span>
          <span className="cart-total-value">{total.toLocaleString('ru-RU')} ₽</span>
        </div>
        {!useBonuses && (
          <div className="cart-row cart-bonus-earn">
            <span className="cart-label">Получите на счёт {BRAND.name}:</span>
            <span className="cart-bonus-earn-value">+{bonusesEarned} ₽</span>
          </div>
        )}
      </div>

      {/* Нет общих дат — детализация по позициям. Открывается по «Перейти к оформлению». */}
      {hasNoDates && showDateConflict && (
        <div data-date-conflict-banner className="cart-section" style={{
          background: '#FFF3CD',
          border: '1px solid #FFCA28',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '12px',
          fontSize: '13px',
          color: '#5D4037',
          lineHeight: '1.5',
          position: 'relative',
        }}>
          <button
            onClick={() => setShowDateConflict(false)}
            title="Скрыть"
            style={{
              position: 'absolute', top: 8, right: 8,
              width: 24, height: 24, border: 'none',
              background: 'rgba(0,0,0,0.06)', borderRadius: '50%',
              cursor: 'pointer', fontSize: 16, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#5D4037',
            }}
          >×</button>
          {(() => {
            // Окна доступности по позициям + действия для сборки заказа в один день.
            const bouquetItems = itemAvailability.filter(it => it.kind === 'bouquet' && it.dates);
            const stemsGroup = itemAvailability.find(it => it.kind === 'stems' && it.dates);
            // Пересечение дат всех букетов (когда все букеты доступны одновременно).
            let bouquetDates = [];
            if (bouquetItems.length > 0) {
              bouquetDates = [...bouquetItems[0].dates].filter(d => bouquetItems.every(b => b.dates.has(d)));
            }
            const bouquetDatesStr = bouquetDates.length ? formatDateRanges(bouquetDates.sort()) : null;
            // Собственное окно стеблей (без букетной нагрузки) — для «убрать букет».
            const stemsOwn = stemsGroup?.ownDates ? [...stemsGroup.ownDates].sort() : [];
            const stemsOwnStr = stemsOwn.length ? formatDateRanges(stemsOwn) : null;

            // available уже округлён до шага продажи (см. stemsDetail). Доступно на даты букета.
            const stemRows = stemsGroup?.stems || [];
            const reducible = stemRows.filter(s => s.available > 0 && s.available < s.requested);

            // Названия букетов (label = «Название» × N → убираем «× N», кавычки уже есть).
            const bouquetNames = bouquetItems.map(b => b.label.replace(/\s×\s\d+$/, ''));
            // Словоформы от настраиваемых названий разделов («Букеты» → «Сеты»)
            const bouquetPluralWord = (catalogLabels.bouquets || 'букеты').toLowerCase();
            const bouquetWord = bouquetItems.length > 1 ? bouquetPluralWord : singularLower(catalogLabels.bouquets);
            const productsWord = (catalogLabels.products || 'стебли').toLowerCase();

            // Цветы-«виновники» — те стебли, что также используются компонентами букетов.
            const conflictNames = stemRows
              .filter(s => s.pid != null && (cartBouquetUsage[s.pid] || 0) > 0)
              .map(s => s.name);
            let conflictPhrase;
            if (conflictNames.length === 0) {
              conflictPhrase = 'один и тот же цветок';
            } else if (conflictNames.length === 1) {
              conflictPhrase = `${obshchiyForm(conflictNames[0])} ${toAccusative(conflictNames[0])}`;
            } else {
              conflictPhrase = `общие цветы — ${conflictNames.map(toAccusative).join(', ')}`;
            }

            const stemsForRemove = stemRows.map(s => `${toAccusative(s.name)} × ${s.requested}`).join(', ');

            // Позиции конфликта с их СОБСТВЕННЫМИ окнами доступности (букеты + стебли).
            const positions = bouquetItems.map(b => ({
              name: b.label.replace(/\s×\s\d+$/, ''),
              dates: b.dates,
            }));
            if (stemsGroup?.perStem?.length) {
              stemsGroup.perStem.forEach(s => positions.push({
                name: s.name, dates: s.dates, rawDates: s.rawDates,
                requested: s.requested, step: s.step, capacity: s.capacity,
              }));
            } else if (stemRows.length > 0 && stemsGroup?.ownDates) {
              positions.push({ name: 'Стебли', dates: stemsGroup.ownDates });
            }
            // Есть ли дата, на которую КАЖДАЯ позиция влезает по отдельности?
            let commonIndiv = [];
            if (positions.length > 0 && positions.every(p => p.dates)) {
              commonIndiv = [...positions[0].dates].filter(d => positions.every(p => p.dates.has(d)));
            }

            // Случай 1 — окна не пересекаются (общей даты нет даже по отдельности).
            // Количество тут ни при чём (дефицит общего цветка уже срезается при добавлении) —
            // позиции просто доступны в разное время. Универсально для N букетов/стеблей.
            // Только стебли — тоже сюда: разные стебли склад не делят, поэтому «вместе нет даты»
            // у них значит «окна не пересекаются».
            if ((commonIndiv.length === 0 || bouquetItems.length === 0) && positions.length >= 2) {
              // Можно собрать, если уменьшить количество стебля (он «уехал» на плановую
              // поставку и разошёлся с остальными) — подсказываем до скольких и на какие даты.
              const reductions = suggestReductions(positions.map(p => ({
                name: p.name, requested: p.requested, step: p.step, capacity: p.capacity,
                dates: p.dates ? [...p.dates] : [],
              })));
              if (reductions.length > 0) {
                return (
                  <>
                    <div style={{ marginBottom: 8, paddingRight: 24 }}>
                      При таком количестве в один заказ не собрать.
                    </div>
                    <ul style={{ margin: '0 0 8px 0', paddingLeft: 18 }}>
                      {reductions.map((r, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          Уменьшите <span style={{ fontWeight: 500 }}>{toAccusative(r.name)}</span> до {r.qty} шт — заказ соберётся {formatDateRanges(r.dates)}
                        </li>
                      ))}
                      <li>Или оформите позиции разными заказами</li>
                    </ul>
                  </>
                );
              }
              // Стебель с сырыми датами сервера: «только на сегодня, заказы на сегодня уже
              // не принимаются», если после отсечки у него остался один сегодняшний день.
              const minIso = isoDate(minOrderDate(
                nowInTZ(slotsConfig.shopTimezone), slotsConfig.timeSlots, slotsConfig.leadTimeMinutes));
              const posRows = positions.map(p => {
                if (p.rawDates) return { name: p.name, datesStr: describeDates(p.rawDates, minIso).datesStr };
                const ds = p.dates ? [...p.dates].sort() : [];
                return { name: p.name, datesStr: ds.length ? formatDateRanges(ds) : 'нет доступных дат' };
              });
              return (
                <>
                  <div style={{ marginBottom: 8, paddingRight: 24 }}>
                    Позиции доступны в разные даты — в одном заказе их не собрать.
                  </div>
                  <ul style={{ margin: '0 0 8px 0', paddingLeft: 18 }}>
                    {posRows.map((p, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{p.name}</span> — {p.datesStr}
                      </li>
                    ))}
                  </ul>
                  <div>Оформите их разными заказами.</div>
                </>
              );
            }

            // Случай 2 — общие даты есть, но на них цветка не хватает на всё количество.
            // Стебли↔букет: «уменьшите стебли до N» / «уберите букеты».
            if (stemRows.length > 0) {
              return (
                <>
                  <div style={{ marginBottom: 8, paddingRight: 24 }}>
                    {capitalizeFirst(bouquetWord)} {bouquetNames.join(', ')} и {productsWord} используют {conflictPhrase} — на одну дату всё не влезает.
                  </div>
                  <ul style={{ margin: '0 0 8px 0', paddingLeft: 18 }}>
                    {bouquetDatesStr && reducible.length > 0 && (
                      <li style={{ marginBottom: 4 }}>
                        Уменьшите{' '}
                        {reducible.map((s, i, arr) => (
                          <span key={i}>
                            <span style={{ fontWeight: 500 }}>{toAccusative(s.name)}</span> до {s.available} шт
                            {i < arr.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                        {' '}— заказ соберётся {bouquetDatesStr}
                      </li>
                    )}
                    {stemsOwnStr && (
                      <li style={{ marginBottom: 4 }}>
                        Или уберите {bouquetWord} {bouquetNames.join(', ')} — {stemsForRemove} доступн{stemRows.length > 1 ? 'ы' : 'а'} {stemsOwnStr}
                      </li>
                    )}
                  </ul>
                </>
              );
            }

            // Случай 2, только букеты с общим цветком на пересекающихся датах.
            const commonStr = commonIndiv.length ? formatDateRanges([...commonIndiv].sort()) : null;
            return (
              <>
                <div style={{ marginBottom: 8, paddingRight: 24 }}>
                  {capitalizeFirst(bouquetPluralWord)} {bouquetNames.join(', ')} используют общие цветы — {commonStr ? `на ${commonStr} ` : ''}хватает не на всё.
                </div>
                <ul style={{ margin: '0 0 8px 0', paddingLeft: 18 }}>
                  <li style={{ marginBottom: 4 }}>
                    Уменьшите количество одного из {(genitivePlural(bouquetPluralWord) || 'них')} — тогда заказ соберётся.
                  </li>
                </ul>
              </>
            );
          })()}
        </div>
      )}

      {/* Минимальная сумма заказа */}
      {!isOrderAmountValid && (
        <div className="cart-section" style={{
          background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
          border: '1px solid #FFB74D',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <HeroIcon name="alert" size={24} color="#E65100" inline />
            <div>
              <div style={{ fontWeight: 600, color: '#E65100', marginBottom: '4px' }}>
                Минимальная сумма заказа: {minOrderAmount.toLocaleString('ru-RU')} ₽
              </div>
              <div style={{ fontSize: '13px', color: '#666' }}>
                Добавьте ещё товаров на {amountDifference.toLocaleString('ru-RU')} ₽
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Кнопка оформления */}
      <div className="cart-section">
        {!getTelegramUser() ? (
          <div style={{ textAlign: 'center', padding: '12px', background: '#FFF3E0', borderRadius: '12px' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              Для оформления заказа откройте приложение через бот @{TG_BOT_USERNAME} в Telegram
            </p>
          </div>
        ) : (
          <button
            className="checkout-btn"
            onClick={async () => {
              // При конфликте дат — показываем баннер и НЕ открываем чекаут. Кнопка
              // сереет до изменения состава (conflictLocked); изменил — снова активна,
              // следующий клик проверяет заново.
              if (hasNoDates) {
                setShowDateConflict(true);
                setConflictCartKey(cartKey);
                // Скроллим к баннеру чтобы клиент его увидел
                setTimeout(() => {
                  const el = document.querySelector('[data-date-conflict-banner]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 50);
                return;
              }
              // Проверяем актуальность корзины перед открытием оформления.
              // Стебли и букеты — раздельные API: id могут совпасть, нельзя слить в один запрос.
              try {
                const activeCart = cart.filter(it => it.quantity > 0 && !it.reserved_by);
                const stemItems = activeCart.filter(it => (it.kind || 'product') === 'product');
                const bouquetItems = activeCart.filter(it => it.kind === 'bouquet');
                const stemIds = stemItems.map(it => it.product_id || it.id).filter(Boolean);
                const bouquetIds = bouquetItems.map(it => it.bouquet_id || it.id).filter(Boolean);

                const [freshProducts, freshBouquets] = await Promise.all([
                  stemIds.length ? api.getProductsByIds(stemIds) : Promise.resolve([]),
                  Promise.all(bouquetIds.map(id => api.getBouquet(id).catch(() => null))),
                ]);
                const productMap = {};
                freshProducts.forEach(p => { productMap[p.id] = p; });
                const bouquetMap = {};
                freshBouquets.forEach(b => { if (b) bouquetMap[b.id] = b; });

                const hiddenItems = [];
                stemItems.forEach(item => {
                  const pid = item.product_id || item.id;
                  const p = productMap[pid];
                  if (!p || p.is_hidden) hiddenItems.push(item);
                });
                bouquetItems.forEach(item => {
                  const bid = item.bouquet_id || item.id;
                  const b = bouquetMap[bid];
                  // Букет «недоступен», если: не пришёл из API, скрыт, неактивен,
                  // ИЛИ today_available=0 И нет ближайшей плановой поставки.
                  if (!b || b.is_hidden || !b.is_active) {
                    hiddenItems.push(item);
                    return;
                  }
                  const today = b.today_available || 0;
                  const nextCap = b.next_better_capacity || 0;
                  if (today === 0 && nextCap === 0) hiddenItems.push(item);
                });
                if (hiddenItems.length > 0) {
                  hiddenItems.forEach(item => {
                    if (item.kind === 'bouquet') {
                      updateBouquetQuantity?.(item.bouquet_id || item.id, -item.quantity);
                    } else {
                      updateQuantity(item.id, -item.quantity);
                    }
                  });
                  setOutOfStockToast({ messages: hiddenItems.map(item => ({ name: item.name, available: 0, gone: true })) });
                  setTimeout(() => setOutOfStockToast(null), 4000);
                  return;
                }
              } catch (e) {
                console.log('Pre-checkout check error:', e);
              }
              const telegramUser = getTelegramUser();
              // Регистрируем юзера только если есть TG/Max ID (мини-аппы).
              // Web-юзер авторизуется отдельно через SMS внутри чекаута.
              if (telegramUser?.telegram_id || telegramUser?.max_id) {
                try {
                  await api.getOrCreateUser(telegramUser);
                } catch (e) {
                  console.log('User registration error:', e);
                }
              }
              const openCheckout = () => {
                const root = document.getElementById('root');
                if (root) root.scrollTop = 0;
                setShowCheckout(true);
                trackEvent('checkout_started');
                trackBeginCheckout(cart, total);
              };
              // Вторая попытка спросить право писать — только если при
              // открытии мини-аппа согласия не дали (иначе не всплывает).
              // Чекаут откроется после ответа на попап.
              if (canAskWriteAccess('checkout')) {
                markWriteAccessAsked('checkout');
                setWriteAccessNext(() => openCheckout);
                return;
              }
              openCheckout();
            }}
            disabled={!canCheckout}
            style={{
              opacity: canCheckout ? 1 : 0.6,
              cursor: canCheckout ? 'pointer' : 'not-allowed',
              background: canCheckout ? 'var(--primary-color)' : '#ccc',
              color: canCheckout ? 'var(--on-primary)' : '#000'
            }}
          >
            {!isOrderAmountValid
              ? `Минимум ${minOrderAmount.toLocaleString('ru-RU')} ₽`
              : 'Перейти к оформлению'
            }
          </button>
        )}
      </div>
      </>
      )}

      {/* ===== СЕКЦИЯ ПОДПИСКИ ===== */}
      {subscriptionCart && subItem && (cartTab === 'subscription' || validCart.length === 0) && (
        <>
          {/* Карточка подписки */}
          <div className="cart-items">
            <div className="cart-item">
              <div className="cart-item-image">
                {subItem.images && subItem.images.length > 0 ? (
                  <img src={getImageUrl(subItem.images[0].image_url)} alt={subItem.name}
                    onClick={() => setPhotoView({ kind: 'sub' })}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }} />
                ) : '💐'}
              </div>
              <div className="cart-item-info">
                <h3 className="cart-item-name">{subItem.name}</h3>
                <div className="cart-item-price">{subItem.price.toLocaleString('ru-RU')} ₽ / доставка</div>
              </div>
              <div className="cart-item-quantity">
                <span className="quantity-btn"
                  onClick={() => {
                    if (subDeliveryCount > 2) {
                      const nc = subDeliveryCount - 1;
                      setSubDeliveryCount(nc);
                      setSubscriptionCart({ ...subscriptionCart, delivery_count: nc });
                    } else {
                      setSubscriptionCart(null);
                    }
                  }}
                >−</span>
                <span className="quantity">{subDeliveryCount}</span>
                <span className={`quantity-btn ${subDeliveryCount >= maxSubDeliveries ? 'disabled' : ''}`}
                  onClick={() => {
                    if (subDeliveryCount < maxSubDeliveries) {
                      const nc = subDeliveryCount + 1;
                      setSubDeliveryCount(nc);
                      setSubscriptionCart({ ...subscriptionCart, delivery_count: nc });
                    }
                  }}
                  style={{ opacity: subDeliveryCount >= maxSubDeliveries ? 0.4 : 1 }}
                >+</span>
              </div>
            </div>
          </div>

          {/* Нечётное количество для подписки */}
          <div className="cart-section" style={{ background: '#FAFAFA', borderRadius: '12px', padding: '14px 16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                <input type="checkbox" checked={subOddQuantity} onChange={(e) => setSubOddQuantity(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <span style={{ fontSize: '14px', color: '#333' }}>Нечётное количество</span>
              </label>
              <div style={{ position: 'relative' }}>
                <span
                  onClick={(e) => { e.stopPropagation(); setShowOddQuantityTooltip(!showOddQuantityTooltip); }}
                  style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', color: 'var(--text-secondary)' }}
                ><FieldIcon name="info" size={17} /></span>
                {showOddQuantityTooltip && (
                  <div style={{
                    position: 'absolute', right: 0, top: '28px', width: '200px',
                    background: '#333', color: '#fff', padding: '10px', borderRadius: '8px',
                    fontSize: '12px', lineHeight: '1.3', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    Сделаем количество стеблей нечётным — уберём один из охапки.
                    <div style={{
                      position: 'absolute', top: '-6px', right: '8px',
                      width: 0, height: 0, borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent', borderBottom: '6px solid #333'
                    }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Сумма подписки */}
          <div className="cart-section">
            <div className="cart-row">
              <span className="cart-label">{subDeliveryCount} {pluralize(subDeliveryCount, 'доставка', 'доставки', 'доставок')} × {subItem.price.toLocaleString('ru-RU')} ₽</span>
              <span className="cart-value">{subSubtotal.toLocaleString('ru-RU')} ₽</span>
            </div>
            <div className="cart-row">
              <span className="cart-label">Сервисный сбор</span>
              <span className="cart-value">{subServiceFee.toLocaleString('ru-RU')} ₽</span>
            </div>
            {subDiscountAmount > 0 && (
              <div className="cart-row">
                <span className="cart-label" style={{ color: '#E65100' }}>Скидка {subDiscountPercent}%</span>
                <span className="cart-value" style={{ color: '#E65100' }}>−{subDiscountAmount.toLocaleString('ru-RU')} ₽</span>
              </div>
            )}
            {subBonusDiscount > 0 && (
              <div className="cart-row">
                <span className="cart-label">Со счёта {BRAND.name}</span>
                <span className="cart-value cart-bonus-discount">−{subBonusDiscount.toLocaleString('ru-RU')} ₽</span>
              </div>
            )}
            {(settings.subscription_delivery_mode || 'free') === 'free' && (
              <div className="cart-row">
                <span className="cart-label">Доставка</span>
                <span className="cart-value" style={{ color: '#4CAF50' }}>Бесплатно</span>
              </div>
            )}
            <div className="cart-row" style={{ alignItems: 'center' }}>
              <span className="cart-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                Замена вазы
                <span style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <span
                    onClick={() => setShowVaseTooltip(!showVaseTooltip)}
                    style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', color: 'var(--text-secondary)', verticalAlign: '-3px' }}
                  ><FieldIcon name="info" size={16} /></span>
                  {showVaseTooltip && (
                    <div style={{
                      position: 'absolute', left: 0, top: 24, width: 220,
                      background: '#333', color: '#fff', padding: 10,
                      borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                      zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}>
                      При первой доставке вы получаете вазу бесплатно. При каждой последующей доставке курьер заменит вазу на новую.
                      <div style={{
                        position: 'absolute', top: -6, left: 8,
                        width: 0, height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: '6px solid #333',
                      }} />
                    </div>
                  )}
                </span>
              </span>
              <span className="cart-value" style={{ color: subDeliveryCount >= vaseMinDeliveries ? '#4CAF50' : '#999' }}>
                {subDeliveryCount >= vaseMinDeliveries ? 'Бесплатно' : `от ${vaseMinDeliveries} дост.`}
              </span>
            </div>
          </div>

          {/* Бонусы для подписки */}
          {userBonuses > 0 && !useBonuses && (
            <div className="cart-section cart-bonus-section">
              <label className="bonus-checkbox">
                <input type="checkbox" checked={subUseBonuses} onChange={(e) => setSubUseBonuses(e.target.checked)} />
                <span className="bonus-checkbox-label">Списать {Math.min(userBonuses, subSubtotal - subDiscountAmount + subServiceFee).toLocaleString('ru-RU')} ₽ со счёта</span>
              </label>
            </div>
          )}

          {/* Итого подписка */}
          <div className="cart-section cart-total-section">
            <div className="cart-row cart-total-row">
              <span className="cart-label">Итого подписка:</span>
              <span className="cart-total-value">{subTotal.toLocaleString('ru-RU')} ₽</span>
            </div>
            {!subUseBonuses && subBonusesEarned > 0 && (
              <div className="cart-row cart-bonus-earn">
                <span className="cart-label">Получите на счёт {BRAND.name}:</span>
                <span className="cart-bonus-earn-value">+{subBonusesEarned} ₽</span>
              </div>
            )}
          </div>

          {/* Кнопка оформить подписку */}
          <div className="cart-section">
            <button
              className="checkout-btn"
              onClick={async () => {
                try {
                  const freshCatalog = await api.getSubscriptionCatalog();
                  const fresh = freshCatalog.find(c => c.id === subItem.id);
                  if (!fresh || !fresh.is_active) {
                    alert('Эта подписка больше недоступна');
                    setSubscriptionCart(null);
                    return;
                  }
                  if (fresh.price !== subItem.price) {
                    alert(`Цена подписки изменилась: ${fresh.price.toLocaleString('ru-RU')} ₽ за доставку`);
                    setSubscriptionCart({ ...subscriptionCart, catalog_item: fresh });
                    return;
                  }
                } catch (e) {
                  console.log('Subscription pre-checkout check error:', e);
                }
                setSubCheckoutData({
                  catalog_item: subItem,
                  delivery_count: subDeliveryCount,
                  discount_percent: subDiscountPercent,
                  subtotal: subSubtotal,
                  discount_amount: subDiscountAmount,
                  service_fee: subServiceFee,
                  total: subTotal,
                  use_bonuses: subUseBonuses,
                  bonus_discount: subBonusDiscount,
                  odd_quantity: subOddQuantity,
                });
                const root = document.getElementById('root');
                if (root) root.scrollTop = 0;
                setShowSubCheckout(true);
              }}
              style={{ background: 'var(--primary-color)', color: 'var(--on-primary)' }}
            >
              Перейти к оформлению
            </button>
          </div>
        </>
      )}


      {/* Toast: товар закончился */}
      {outOfStockToast && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '320px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <div style={{ marginBottom: '4px' }}>
              <HeroIcon name="soldOut" size={44} />
            </div>
            {outOfStockToast.messages.map((m, i) => (
              <p key={i} style={{ margin: '0 0 8px 0', color: '#333', fontSize: '16px', lineHeight: '1.4' }}>
                {m.gone
                  ? `${m.name} — раскупили`
                  : `${m.name} — осталось ${m.available} шт`
                }
              </p>
            ))}
            <p style={{ margin: '12px 0 0 0', color: '#999', fontSize: '14px' }}>
              Кто-то вас опередил ;(
            </p>
          </div>
        </div>
      )}

      {/* Разрешение боту писать — ступень «Оформить» (только TG без Старта,
          не согласившимся при открытии). Чекаут откроется после ответа. */}
      {writeAccessNext && (
        <NotifyPermissionPopup
          isOpen
          stage="checkout"
          onAllow={() => { const next = writeAccessNext; setWriteAccessNext(null); requestWriteAccessNative(); next(); }}
          onLater={() => { const next = writeAccessNext; setWriteAccessNext(null); next(); }}
        />
      )}
    </div>
  );
}

export default CartPage;
